/* eslint-disable @typescript-eslint/no-this-alias, @typescript-eslint/no-explicit-any */
import L from "leaflet";

// owner
import { forceMapFilters } from "@cleanup/components/AdvancedFilters/FilterConstants";
import { ClusterIcon } from "@/components/Leaflet/Map/Marker/ClusterIcon";
import { ICON_TYPE, svgIcon } from "@/components/Leaflet/Map/Marker/SvgIcon";
import {
  PruneCluster,
  PruneClusterForLeaflet,
} from "@/components/Leaflet/Map/PruneCluster/PruneCluster";
import GlobalUtils from "@/utils/GlobalUtils";

import searchUIClient from "@/apis/searchUIClient";
import DeviceIcon from "@images/device/Device-icon.svg";
import Cluster_Icon from "@images/map/cluster-icon.svg";
import configurationUtils from "@/utils/IconConfiguration";

interface DeviceData {
  id: number;
  name: string;
  currentLat: number;
  currentLng: number;
  lastSeenAlive?: string;
  lastNetworkLocationRadius?: number;
  decoratorColor?: string;
  location?: string;
  lastBeaconName?: string;
  lastActivity?: string;
  lastLocationSource?: string;
  trackerIcon?: string;
  coordinate?: {
    lat: number;
    lon: number;
    lastSeen?: string;
  };
}

interface AssetLayerOptions {
  ignoreTrackerId?: number;
}

interface SearchQuery {
  searchQueryType: string;
  queryObjectType: string;
  query: Array<{
    categoryId: string;
    filterKey: string;
    filterType: string;
    searchType: string;
    notFilter: boolean;
    filterValue: {
      includeEmpty: boolean;
      includeNoEmpty: boolean;
      selectedValues: unknown[];
    };
  }>;
  requestedTableColumns: Array<{
    columnKey: string;
    order: null;
  }>;
}

const AssetLayerClass = (L.FeatureGroup as any).extend({
  options: {},
  bounds: L.latLngBounds([]),
  pruneCluster: null as any,

  disableGroup: function (isDisabled: boolean) {
    const self = this;
    if (self.pruneCluster) {
      if (isDisabled) {
        self.pruneCluster.Cluster.Size = 0.0000000001;
      } else {
        self.pruneCluster.Cluster.Size = 87;
      }
      self.pruneCluster.ProcessView();
    }
  },

  updateData: function (orgId: number | null, options?: AssetLayerOptions) {
    const self = this;
    const pruneCluster = new PruneClusterForLeaflet(10, 10);

    self.pruneCluster = pruneCluster;
    PruneCluster.Cluster.ENABLE_MARKERS_LIST = true;

    pruneCluster.BuildLeafletClusterIcon = function (cluster: any) {
      const _length = cluster.population;
      return ClusterIcon({
        count: _length,
        iconUrl: Cluster_Icon,
        iconClassName: "cluster-svg",
      } as any) as any;
    };

    pruneCluster.PrepareLeafletMarker = function (leafletMarker: any, data: any) {
      data.icon.setMarker(leafletMarker);
      leafletMarker.setIcon(data.icon);
      if (data.tooltip) {
        leafletMarker.unbindTooltip();
        leafletMarker.bindTooltip(data.tooltip, {
          permanent: true,
          direction: "top",
          className: "transparent-tooltip",
          offset: [0, -8],
        });
      }
    };

    const basic: SearchQuery = {
      searchQueryType: "BASIC",
      queryObjectType: "SIGFOX_DEVICE",
      query: [],
      requestedTableColumns: [
        {
          columnKey: "lastNetworkLocationRadius",
          order: null,
        },
        {
          columnKey: "currentLat",
          order: null,
        },
        {
          columnKey: "currentLng",
          order: null,
        },
        {
          columnKey: "name",
          order: null,
        },
        {
          columnKey: "decoratorColor",
          order: null,
        },
        {
          columnKey: "location",
          order: null,
        },
        {
          columnKey: "lastBeaconName",
          order: null,
        },
        {
          columnKey: "lastActivity",
          order: null,
        },
      ],
    };

    forceMapFilters(basic as any, false);

    self.bound = L.latLngBounds([]);
    self.clearLayers();

    self.addLayer(self.pruneCluster);

    void searchUIClient.search(basic as any).then(({ data }: any) => {
      self.processData(data.data, options);
    });
  },

  transformData: function (beacon: DeviceData) {
    beacon.coordinate = {
      lat: beacon.currentLat,
      lon: beacon.currentLng,
      lastSeen: beacon.lastSeenAlive,
    };
  },

  isValidCoordinate: function (
    coordinate: { lat: number; lon: number } | undefined
  ): boolean {
    return !!(coordinate && (coordinate.lat || coordinate.lon));
  },

  processData: function (data: DeviceData[], options?: AssetLayerOptions) {
    const self = this;
    const lastMarkers: DeviceData[] = [];
    data.forEach((obj: DeviceData) => {
      if (options && options.ignoreTrackerId === obj.id) return;
      self.transformData(obj);
      const coordinate = obj.coordinate;
      const isValid = self.isValidCoordinate(coordinate);
      if (isValid) {
        lastMarkers.push(obj);
      }
    });
    self.processMarkers(lastMarkers);
  },

  processMarkers: function (lastMarkers: DeviceData[]) {
    const self = this;
    const markersById = new Map(lastMarkers.map((m) => [m.id, m]));

    // register the remaining markers
    markersById.forEach((device) => {
      // new marker, add to cluster
      const _coordinate = device.coordinate;
      if (!_coordinate) return;
      const activityState = configurationUtils.getActivityState(device as any);
      const state = configurationUtils.getState(device as any);
      const highestSeverity = configurationUtils.getHighestAlertSeverity(device as any);

      const icon = svgIcon({
        iconUrl: device.trackerIcon
          ? GlobalUtils.getFile(
              "device",
              device.id,
              "icon",
              device.trackerIcon,
              "small"
            )
          : DeviceIcon,
        id: String(device.id),
        trackerName: device.name,
        activityState: activityState,
        state: state,
        network: device?.lastLocationSource === "network",
        alertSeverity: highestSeverity,
        decoratorColor: device?.decoratorColor,
        type: ICON_TYPE.assetType,
      });

      const marker = new PruneCluster.Marker(
        _coordinate.lat || 0,
        _coordinate.lon || 0,
        {
          icon: icon,
          tooltip: device.name,
        }
      );
      marker.data.deviceInfo = device;
      (marker as any).getLatLng = function () {
        return new L.LatLng(this.position.lat, this.position.lon);
      };

      self.pruneCluster.RegisterMarker(marker);
    });

    self.pruneCluster.ProcessView();
  },

  getLayer: function (): any {
    return this;
  },

  getBounds: function (): L.LatLngBounds {
    return this.bounds;
  },
});

(L as any).AssetLayer = AssetLayerClass;
(L as any).assetLayer = function (opts?: L.LayerOptions): any {
  return new AssetLayerClass(opts);
};

export const AssetLayer = (L as any).assetLayer;
